import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './DebtDetails.module.scss';
import { fetchDebtDetailsData, fetchDebtDocsData } from '../../../../store/dashboard/debtDetails/debtDetails_apiHelper';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { downloadFile, getPresignedUrl } from '../../../Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { ctaPurpleRight } from '../../../../assets/hostedassets';
import CtaPill from '../../../../common/_custom/CtaPill/CtaPill';
import { EXCEL_ANALYSIS_LOADING_LABEL, REFERENCE_DOCUMENT_LABEL } from '../../../../common/_custom/CtaPill/ctaPillConstants';
import { useParams } from 'react-router-dom';

import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { isEmpty, truncateByCharacters } from '../../../../utils/utils';
import FileViewerDrawer from '../../../../common/DocumentViewer/FileViewerDrawer';
import { EXT_MIME, PREVIEWABLE_MIME_TYPES } from '../../../../common/DocumentViewer/constants';
import { toast } from 'react-toastify';

const headers = [
    {
        label: 'Lender Name',
        key: 'lenderName',
    },
    {
        label: 'Loan Type',
        key: 'typeOfDebtLoan',
    },
    {
        label: 'Disbursal On',
        key: 'disbursalDate',
    },
    {
        label: 'Sanctioned',
        key: 'sanctionedAmount',
    },
    {
        label: 'Disbursed',
        key: 'disbursedAmount',
    },
    {
        label: 'O/S Balance',
        key: 'outstandingBalance',
    },
    {
        label: 'EMI',
        key: 'emiAmount',
    },
    {
        label: 'ROI (%)',
        key: 'roi_text',
    },
    {
        label: 'Remaining Tenure*',
        key: 'remainingTenure',
    },
    {
        label: 'Security',
        key: 'security',
    },
];

const SECURITY_TRUNCATE_THRESHOLD = 35;

/** Numeric ROI → two decimals; `roi_text` strings (e.g. "10% p.a.") → as-is; empty → dash */
function formatRoiCellValue(raw: unknown): string {
    if (isEmpty(raw)) return '-';
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
        return raw.toFixed(2);
    }
    return String(raw);
}

function DebtDetails() {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [debtDetailsData, setDebtDetailsData] = useState<{ rows: any; totals: any } | null>(null);
    const [debtDocs, setDebtDocs] = useState<any>({ debtDocsAica: [], debtDocsRrf: [] });
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [filePreview, setFilePreview] = useState<{ url: string; mimeType: string } | null>(null);
    const [openingPreview, setOpeningPreview] = useState(false);
    const [downloadingFromDrawer, setDownloadingFromDrawer] = useState(false);
    const [securityPopup, setSecurityPopup] = useState<{ text: string; top: number; left: number } | null>(null);
    const securityHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debtDocsAicaLength = debtDocs?.debtDocsAica?.length;

    useEffect(() => {
        fetchData();
        fetchDebtDocs();
    }, [params.id]);

    const fetchData = () => {
        setLoading(true);
        fetchDebtDetailsData({
            orgId: params.id as string,
        })
            .then((data) => {
                setDebtDetailsData({
                    rows: data?.rows,
                    totals: data?.totals,
                });
                setLastUpdatedAt(data?.updatedAt);
            })
            .catch((err) => {
                console.error('Error in fetching debt schedule data', err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchDebtDocs = () => {
        fetchDebtDocsData({
            orgId: params.id as string,
        })
            .then((data) => {
                setDebtDocs({
                    debtDocsAica: data?.debtDocsAica,
                    debtDocsRrf: data?.debtDocsRrf,
                });
            })
            .catch((err) => {
                console.error('Error in fetching debt schedule latest doc', err);
            })
            .finally(() => {});
    };

    const getDebtDocForDownload = () => {
        const docIdFromData = debtDetailsData?.rows?.[0]?.docId;
        if (docIdFromData)
            return {
                docId: docIdFromData as string,
                docName: '',
            };
        else if (debtDocsAicaLength)
            return {
                docId: debtDocs?.debtDocsAica?.[debtDocsAicaLength - 1]?.docId as string,
                docName: debtDocs?.debtDocsAica?.[debtDocsAicaLength - 1]?.docName as string,
            };
        else return null;
        // else if(debtDocsRrfLength) return debtDocs?.debtDocsRrf?.[debtDocsRrfLength - 1]
        // else return null;
    };

    const getRowData = (keyName: string, rowData) => {
        switch (keyName) {
            case 'emiAmount':
            case 'sanctionedAmount':
            case 'disbursedAmount':
            case 'outstandingBalance':
                return Number.isNaN(parseInt(rowData?.[keyName])) ? '-' : `₹${parseInt(rowData?.[keyName]).toLocaleString('en-IN')}`;
            case 'remainingTenure':
                return Number.isNaN(parseFloat(rowData?.[keyName])) ? '-' : rowData?.[keyName];
            case 'disbursalDate':
                return dayjs(rowData?.[keyName])?.isValid() ? dayjs(rowData?.[keyName]).format('DD MMM YYYY') : '-';
            case 'roi':
            case 'roi_text':
                return formatRoiCellValue(rowData?.[keyName]);
            case 'security': {
                const sec = rowData?.security;
                return typeof sec === 'string' ? sec.trim() || '-' : sec || '-';
            }
            default:
                return rowData?.[keyName] || '-';
        }
    };

    const parseValidationAlerts = (rawValidationAlerts: any): Record<string, string | string[]> => {
        if (!rawValidationAlerts) return {};

        switch (typeof rawValidationAlerts) {
            case 'string':
                try {
                    return JSON.parse(rawValidationAlerts);
                } catch (e) {
                    return {};
                }
            case 'object':
                return rawValidationAlerts;
            default:
                return {};
        }
    };

    const getValidationMessageForCell = (keyName: string, rowData: any): string => {
        const validationAlerts = parseValidationAlerts(rowData?.validationAlerts);
        const cellAlert = validationAlerts?.[keyName];

        if (!cellAlert) return '';
        if (Array.isArray(cellAlert)) return cellAlert.filter(Boolean).join('\n');
        return String(cellAlert);
    };

    const renderValidationTooltipContent = (message: string) => (
        <div className={styles.ValidationTooltipContent}>
            {message.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
            ))}
        </div>
    );

    const renderCellData = (keyName: string, rowData: any) => {
        const value = getRowData(keyName, rowData);
        const validationMessage = getValidationMessageForCell(keyName, rowData);

        if (validationMessage && value !== '-') {
            return (
                <Tooltip content={renderValidationTooltipContent(validationMessage)} direction="top" usePortal fitContent>
                    <span className={styles.FlaggedCell}>
                        <span className={styles.FlaggedCellText}>{value}</span>
                    </span>
                </Tooltip>
            );
        }

        if (keyName === 'security' && value !== '-' && value.length > SECURITY_TRUNCATE_THRESHOLD) {
            return (
                <span className={styles.SecurityCellContent}>
                    <span className={styles.SecurityValueText}>{truncateByCharacters(value, SECURITY_TRUNCATE_THRESHOLD)}</span>
                    <span
                        className={styles.SeeMoreLink}
                        onMouseEnter={(e) => {
                            if (securityHideTimer.current) clearTimeout(securityHideTimer.current);
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setSecurityPopup({ text: value, top: rect.top + rect.height / 2, left: rect.left });
                        }}
                        onMouseLeave={() => {
                            securityHideTimer.current = setTimeout(() => setSecurityPopup(null), 100);
                        }}
                    >
                        See more
                    </span>
                </span>
            );
        }

        return value;
    };

    const getTotalRowData = (keyName: string) => {
        const totalOutstanding = debtDetailsData?.totals?.outstandingBalance;
        const totalSanctioned = debtDetailsData?.totals?.sanctionedAmount;
        const totalDisbursed = debtDetailsData?.totals?.disbursedAmount;
        const totalEmi = debtDetailsData?.totals?.emiAmount;
        switch (keyName) {
            case 'lenderName':
                return 'Total';
            case 'outstandingBalance':
                return totalOutstanding ? `₹${parseInt(totalOutstanding)?.toLocaleString('en-IN')}` : '-';
            case 'sanctionedAmount':
                return totalSanctioned ? `₹${parseInt(totalSanctioned)?.toLocaleString('en-IN')}` : '-';
            case 'disbursedAmount':
                return totalDisbursed ? `₹${parseInt(totalDisbursed)?.toLocaleString('en-IN')}` : '-';
            case 'emiAmount':
                return totalEmi ? `₹${parseInt(totalEmi)?.toLocaleString('en-IN')}` : '-';
            case 'security':
                return '-';
            default:
                return '-';
        }
    };

    const downloadDebtDoc = async () => {
        const docToDownload: any = getDebtDocForDownload();
        if (docToDownload?.docId) {
            setDownloading(true);
            downloadFile(docToDownload?.docId, () => {}, docToDownload?.docName).finally(() => {
                setDownloading(false);
            });
        }
    };

    const viewDebtDoc = async () => {
        const docToView: any = getDebtDocForDownload();
        if (!docToView?.docId) return;
        setOpeningPreview(true);
        try {
            const res = await getPresignedUrl({ docId: Number(docToView.docId) }, false);
            if (!res) {
                toast.error('Error in fetching file');
                return;
            }
            const ext = (docToView.docName || '').split('.').pop()?.toLowerCase() ?? '';
            const apiMimeType = res.fileType?.split(';')[0]?.trim().toLowerCase() ?? '';
            const effectiveMimeType = EXT_MIME[ext] ?? apiMimeType;
            if (!PREVIEWABLE_MIME_TYPES.includes(effectiveMimeType) && !effectiveMimeType?.startsWith('image/')) {
                await downloadFile(docToView.docId, () => {}, docToView.docName);
                return;
            }
            setFilePreview({ url: res.uri, mimeType: effectiveMimeType });
        } catch (e) {
            toast.error('Error in fetching file');
        } finally {
            setOpeningPreview(false);
        }
    };

    const downloadFromDrawer = async () => {
        const docToDownload: any = getDebtDocForDownload();
        if (!docToDownload?.docId) return;
        setDownloadingFromDrawer(true);
        downloadFile(docToDownload.docId, () => {}, docToDownload.docName).finally(() => {
            setDownloadingFromDrawer(false);
        });
    };

    const isRightAlignedKey = (key: string) => ['roi', 'roi_text', 'remainingTenure'].includes(key);

    const formattedAsOfDate = lastUpdatedAt && dayjs(lastUpdatedAt).isValid() ? dayjs(lastUpdatedAt).format('DD MMMM YYYY') : '';

    const visibleHeaders = headers.filter((header) => {
        if (header.key !== 'security') return true;
        return Boolean(
            debtDetailsData?.rows?.some((row: any) => {
                const security = row?.security;
                if (typeof security === 'string') return security.trim().length > 0;
                return !isEmpty(security);
            }),
        );
    });

    return (
        <div className={styles.DebtDetails}>
            {!!debtDetailsData?.rows?.length && (
                <div className={styles.DebtDetailsTable}>
                    <div className={styles.CardHeader}>
                        <span className={styles.CardHeading}>Debt Details</span>
                        <div className={styles.CardHeaderRight}>
                            {formattedAsOfDate && <span className={styles.AsOfDate}>As of date {formattedAsOfDate}</span>}
                            {getDebtDocForDownload() && (
                                <CtaPill
                                    icon={ctaPurpleRight}
                                    label={REFERENCE_DOCUMENT_LABEL}
                                    loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                                    loading={openingPreview}
                                    onClick={viewDebtDoc}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles.CardBody}>
                        <div className={styles.TableContainer}>
                            <table className={styles.Table}>
                                <thead className={styles.TableHead}>
                                    <tr className={styles.TableHeadRow}>
                                        {visibleHeaders.map((header, index) => (
                                            <th
                                                key={index}
                                                className={clsx(styles.HeadRowItem, {
                                                    [styles.FirstHeadRowItem]: index === 0,
                                                    [styles.RightAlign]: isRightAlignedKey(header.key),
                                                })}
                                            >
                                                {header.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className={styles.TableBody}>
                                    {debtDetailsData?.rows?.map((rowData, i) => (
                                        <tr className={styles.TableBodyRow} key={i}>
                                            {visibleHeaders.map((header, j) => (
                                                <td
                                                    className={clsx(styles.RowItem, {
                                                        [styles.FirstColumnItem]: j === 0,
                                                        [styles.RightAlign]: isRightAlignedKey(header.key),
                                                        [styles.SecurityCell]: header.key === 'security',
                                                    })}
                                                    key={j}
                                                >
                                                    {renderCellData(header.key, rowData)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    <tr className={styles.TableBodyRow}>
                                        {visibleHeaders.map((header, i) => (
                                            <td
                                                className={clsx(styles.RowItem, styles.TotalRowItem, {
                                                    [styles.FirstColumnItem]: i === 0,
                                                    [styles.RightAlign]: isRightAlignedKey(header.key),
                                                })}
                                                key={i}
                                            >
                                                {getTotalRowData(header.key)}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.NoteText}>
                            <span className={styles.Bold}>*Note:</span> Remaining Tenure is in months unless specified
                        </div>
                    </div>
                </div>
            )}
            {!!filePreview && (
                <FileViewerDrawer
                    heading={getDebtDocForDownload()?.docName || 'Debt Schedule'}
                    fileUrl={filePreview.url}
                    mimeType={filePreview.mimeType}
                    downloading={downloadingFromDrawer}
                    onDownload={downloadFromDrawer}
                    onClose={() => setFilePreview(null)}
                />
            )}
            {!!securityPopup &&
                createPortal(
                    <div
                        className={styles.SecurityPopup}
                        style={{ top: securityPopup.top, left: securityPopup.left }}
                        onMouseEnter={() => {
                            if (securityHideTimer.current) clearTimeout(securityHideTimer.current);
                        }}
                        onMouseLeave={() => {
                            securityHideTimer.current = setTimeout(() => setSecurityPopup(null), 100);
                        }}
                    >
                        {securityPopup.text}
                    </div>,
                    document.body,
                )}
        </div>
    );
}

export default DebtDetails;
